import { FormEventHandler, useEffect, useState } from 'react';
import { useSearchParams as useURLSearchParams } from 'react-router-dom';

export interface SearchProps {
  searchQuery?: string;
  page?: number;
  onChangePage?: (page: number) => void;
}

export interface Search {
  searchQuery: string;
  initialSearchQuery: string;
  currentSearchQuery: string;
  page: number;
  handleSearchSubmit: FormEventHandler<HTMLFormElement>;
  handlePageChange: (page: number) => void;
  setSearchQuery: (value: string) => void;
}

interface SearchParams {
  searchQuery: string;
  page: number;
}

const useSearch = (): Search => {
  const [searchParams, setSearchParams] = useURLSearchParams();

  const searchQueryParam = searchParams.get('query') || '';
  const pageParam = Number(searchParams.get('page')) || 1;

  const [searchQuery, setSearchQuery] = useState<string>(searchQueryParam);

  const updateSearchParams = (params: Partial<SearchParams>): void => {
    const newParams = new URLSearchParams(searchParams);

    if (params.searchQuery !== undefined) {
      newParams.set('query', params.searchQuery);
      setSearchQuery(params.searchQuery);
    }

    if (params.page !== undefined) {
      newParams.set('page', String(params.page));
    }

    setSearchParams(newParams);
  };

  const handleSearchSubmit: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    updateSearchParams({ searchQuery, page: 1 });
  };

  const scrollToElementWithOffset = (elementId: string, offsetRem: number = 1) => {
    const element = document.getElementById(elementId);
    const rem = parseFloat(getComputedStyle(document.documentElement).fontSize);
    const offset = -rem * offsetRem;

    if (element) {
      const y = element.getBoundingClientRect().top + window.scrollY + offset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePageChange = (page: number) => {
    updateSearchParams({ page });

    scrollToElementWithOffset('product-catalog-table');
  };

  useEffect(() => {
    setSearchQuery((prevSearchQuery) => {
      if (searchQueryParam === prevSearchQuery) {
        return prevSearchQuery;
      }

      return searchQueryParam;
    });
  }, [searchQueryParam]);

  return {
    searchQuery: searchQueryParam,
    initialSearchQuery: searchQueryParam,
    currentSearchQuery: searchQuery,
    page: pageParam,
    handleSearchSubmit,
    handlePageChange,
    setSearchQuery,
  };
};

export default useSearch;
